import { downloadFile, fetchBlobUrl, request } from '../lib/apiClient'
import { toDate } from '../lib/format'

/**
 * Job requisitions — backend/job/app.py `GET /jobs/fetch`.
 *
 * Distinct from api/jobPosts.js on purpose: that serves the Matching and CV-import
 * PICKERS off /job/fetch (job_board.view). This is the requisition list behind
 * jobs.view, and carries department / vacancies / requisition_status, which the
 * picker payload does not.
 */
/** Matching applicants for a job: latest run (progress) + those who passed ATS. */
export function fetchMatches(jobPostId) {
  return request('/jobs/matches/fetch', { params: { job_post_id: jobPostId } })
}

/** Find matches (shows saved results if already run) or Find more (`more`: next 25). */
export function startMatches(jobPostId, more = false) {
  return request('/jobs/matches/start', { method: 'POST', params: { job_post_id: jobPostId, more: more || undefined } })
}

/* The Jobs list filters -> GET /jobs/fetch and /jobs/export query params.
   Every filter but the vacancy/date bounds takes one value or an array (any-of);
   createdFrom / createdBefore are ISO datetimes, createdBefore exclusive. */
function listFilterParams({ search, department, requisitionStatus, employmentType, hiringManagerId, priority,
                            location, platform, recruiterId, departmentHeadId,
                            vacanciesMin, vacanciesMax, createdFrom, createdBefore, activeOnly } = {}) {
  return {
    search,
    department,
    requisition_status: requisitionStatus,
    employment_type: employmentType,
    hiring_manager_id: hiringManagerId,
    priority,
    location,
    platform,
    recruiter_id: recruiterId,
    department_head_id: departmentHeadId,
    vacancies_min: vacanciesMin,
    vacancies_max: vacanciesMax,
    created_from: createdFrom,
    created_before: createdBefore,
    active_only: activeOnly,
  }
}

export function list({ top, skip, ...filters } = {}) {
  return request('/jobs/fetch', { params: { ...listFilterParams(filters), top, skip } })
}

/** Jobs filter panel options, over the jobs the caller can see —
 *  `{data:{departments, locations, platforms, employment_types: [str],
 *          hiring_managers, recruiters, department_heads: [{id, name}]}}`. */
export function filterOptions() {
  return request('/jobs/filter-options/fetch')
}

/* requisition_status is the HIRING lifecycle. The row's separate `status` field is
   the Buffer publishing lifecycle — never map the two onto one badge. Fallback
   matches GET /jobs/requisition-statuses/fetch so the dropdown still works if
   that call 403s. */
export const REQUISITION_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed', label: 'Closed' },
  { value: 'completed', label: 'Completed' },
]
export const REQ_STATUS_LABEL = Object.fromEntries(REQUISITION_STATUSES.map((s) => [s.value, s.label]))
const LABEL_TO_STATUS = Object.fromEntries(REQUISITION_STATUSES.map((s) => [s.label, s.value]))
export const JOB_STATUSES = REQUISITION_STATUSES.map((s) => s.label)

/** Who is HIRED on one job, newest first: GET /jobs/hired/fetch. */
export function fetchHired(jobPostId) {
  return request('/jobs/hired/fetch', { params: { job_post_id: jobPostId } }).then((r) => {
    const d = r?.data ?? {}
    return {
      vacancies: d.vacancies ?? 0,
      hired: (d.hired ?? []).map((p) => ({
        userId: p.user_id,
        name: p.name,
        email: p.email,
        avatarUrl: p.avatar_url || null,
        avatarViewUrl: p.avatar_view_url || null,
        hiredAt: p.hired_at ? new Date(p.hired_at) : null,
      })),
    }
  })
}

export function listRequisitionStatuses() {
  return request('/jobs/requisition-statuses/fetch')
}

/**
 * Distinct departments across all requisitions (closed included — a past
 * hiring department is a legitimate lens on a past window). The shared option
 * list for the Dashboard and Analytics department filters: both screens cache
 * it under qk.jobs.list({ scope: 'departments' }), and React Query caches on
 * the key alone, so every consumer of that key must use THIS fetcher — two
 * queryFns returning different shapes on one key overwrite each other (the
 * React error #31 failure documented in api/inbox.js).
 */
export async function fetchDepartmentOptions() {
  // filterOptions reads every job the caller can see (closed included), distinct
  // on the server — not the first 500 jobs.
  const res = await filterOptions()
  const departments = Array.isArray(res?.data?.departments) ? res.data.departments : []
  return [...new Set(departments.filter(Boolean))].sort()
}

function experienceLabel(min, max) {
  if (min == null && max == null) return null
  if (min != null && max != null) return `${min}–${max} years`
  return `${min ?? max}+ years`
}

/* Job ownership arrives in one of two shapes. serialize_job_row (GET /jobs/fetch,
   GET /jobs/profile/fetch) sends the two roles as separate {id: name} objects:

     "recruiters": {"ed9e…": "Nida Khan"}, "hiring_manager": {"77aa…": "Amara Osei"}

   serialize_job_post — the inbox / matching / picker payload — still sends the
   older flat keys (current_recruiter_ids, recruiter_names, recruiters as an
   ARRAY of {id, name}, hiring_manager_id / hiring_manager_name). Both are read
   here so one mapper serves every caller; the Array check is what tells the two
   `recruiters` shapes apart. */
function recruiterMap(row) {
  const value = row?.recruiters
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null
}

function recruiterIdsFrom(row) {
  const map = recruiterMap(row)
  if (map) return Object.keys(map).map(String)
  const ids = Array.isArray(row?.current_recruiter_ids)
    ? row.current_recruiter_ids.filter(Boolean).map(String)
    : []
  if (ids.length) return ids
  return row?.current_recruiter_id ? [String(row.current_recruiter_id)] : []
}

function recruiterNamesFrom(row) {
  const map = recruiterMap(row)
  if (map) return Object.values(map).filter(Boolean)
  if (Array.isArray(row?.recruiter_names) && row.recruiter_names.length) {
    return row.recruiter_names.filter(Boolean)
  }
  if (Array.isArray(row?.recruiters) && row.recruiters.length) {
    return row.recruiters.map((r) => r?.name).filter(Boolean)
  }
  return row?.recruiter_name ? [row.recruiter_name] : []
}

/** {id, name} of the hiring manager, from either payload shape. */
function hiringManagerFrom(row) {
  const value = row?.hiring_manager
  if (value && typeof value === 'object') {
    const [id, name] = Object.entries(value)[0] ?? []
    if (id) return { id: String(id), name: name ?? null }
  }
  return {
    id: row?.hiring_manager_id ? String(row.hiring_manager_id) : null,
    name: row?.hiring_manager_name ?? null,
  }
}

/** Display names from `effective_recruiters` (stored recruiters, or the hiring
    manager when none are stored). Older payloads lack it: fall back to stored. */
function effectiveRecruiterNamesFrom(row, storedNames) {
  const value = row?.effective_recruiters
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const names = Object.values(value).filter(Boolean)
    if (names.length) return names
  }
  return storedNames
}

/** API row -> what the Jobs table and detail modal render. */
export function toJobView(row) {
  const recruiterIds = recruiterIdsFrom(row)
  const recruiterNames = recruiterNamesFrom(row)
  const manager = hiringManagerFrom(row)
  const displayNames = effectiveRecruiterNamesFrom(row, recruiterNames)
  return {
    id: row.id,
    title: row.title,
    department: row.department,
    departmentId: row.department_id || null,
    location: row.location,
    type: row.employment_type,
    vacancies: row.vacancies,
    priority: row.priority || 'medium',
    hired: row.hired ?? 0, // filled vacancies: applicants currently HIRED
    budget: row.budget ?? '',
    platform: row.platform || null,
    status: REQ_STATUS_LABEL[row.requisition_status] ?? row.requisition_status,
    publishStatus: row.status,
    // Raw stored recruiters: the edit form reads and writes these, so they must
    // never carry the hiring-manager fallback. Display uses displayRecruiter.
    recruiter: recruiterNames.join(', ') || null,
    recruiterId: recruiterIds[0] || null,
    recruiterIds,
    recruiterNames,
    displayRecruiter: displayNames.join(', ') || null,
    recruiterIsManager: Boolean(row.recruiter_is_hiring_manager) && !recruiterNames.length,
    hiringManager: manager.name,
    hiringManagerId: manager.id,
    createdByName: row.created_by_name,
    applicantCount: row.applicant_count ?? 0,
    // A real Date, so the existing `sortValue: j => j.created.getTime()` keeps working.
    created: toDate(row.created_at),
    closedAt: toDate(row.closed_at),
    requisitionStatus: row.requisition_status,
    experienceMin: row.experience_min,
    experienceMax: row.experience_max,
    experience: experienceLabel(row.experience_min, row.experience_max),
    skills: row.requirements ?? [],
    optionalSkills: row.optional_skills ?? [],
    description: row.description,
    requisitionId: row.requisition_id || null,
    requisitionTitle: row.requisition_title || '',
    requisitionDepartment: row.requisition_department || '',
    requisitionLabel: row.requisition_id
      ? `${(row.requisition_title || 'Untitled').trim() || 'Untitled'} - ${(row.requisition_department || '—').trim() || '—'}`
      : null,
  }
}

/**
 * Styled .xlsx download of the requisition list — GET /jobs/export
 * (jobs.export). Same filters as list(); `status` takes the UI label.
 * downloadFile triggers the browser save from the Content-Disposition name.
 */
export function exportXlsx({ status, ...filters } = {}) {
  // `status` is the older label-or-value form; requisitionStatus passes straight through.
  const legacy = status ? { requisitionStatus: LABEL_TO_STATUS[status] ?? status } : {}
  return downloadFile('/jobs/export', { params: listFilterParams({ ...legacy, ...filters }) })
}

export function update(jobPostId, body) {
  return request('/jobs/update', {
    method: 'PATCH',
    params: { job_post_id: jobPostId },
    body,
  })
}

export function remove(jobPostId) {
  return request('/jobs/delete', {
    method: 'DELETE',
    params: { job_post_id: jobPostId },
  })
}

/** Attach or replace a job post's cover image — POST /job/image/upload (multipart). */
export function uploadImage(jobPostId, file) {
  const fd = new FormData()
  fd.append('job_post_id', jobPostId)
  fd.append('file', file)
  return request('/job/image/upload', { method: 'POST', body: fd })
}

/** Object URL of the cover image, or null when the post has none. Caller revokes. */
export function fetchImageUrl(jobPostId) {
  return fetchBlobUrl('/job/image/fetch', { params: { job_post_id: jobPostId } })
}

/** `status` is the Jobs UI label (Open / Closed / On Hold) or a raw requisition_status. */
export function setStatus(jobPostId, status) {
  const requisition_status = LABEL_TO_STATUS[status] ?? status
  return request('/jobs/status', {
    method: 'PATCH',
    params: { job_post_id: jobPostId },
    body: { requisition_status },
  })
}

/**
 * Job profile page — GET /jobs/profile/fetch. One round trip: the requisition
 * row (same shape as /jobs/fetch, so toJobView applies), its suggested
 * candidates (newest ats_results row per person, best score first) and the
 * Suggested / Top Match header stats.
 */
export function fetchProfile(jobPostId, { search, top, limit, skip } = {}) {
  return request('/jobs/profile/fetch', {
    params: { job_post_id: jobPostId, search, top, limit, skip },
  })
}

/** ats_results.band labels, best first — the backend's MATCH_BANDS. */
export const MATCH_BANDS = ['Strong Match', 'Potential Match', 'Weak Match']

export const SUGGESTED_SOURCE_LABEL = { inbox: 'Email', form: 'Sheet Form', upload: 'Uploaded', bank: 'CV Bank' }

/** One suggested candidate -> what the job profile card renders. */
export function toSuggestedView(row) {
  return {
    id: row.id,
    userId: row.user_id || null,
    scoredCandidateId: row.scored_candidate_id || null,
    name: row.name || row.email || 'Unknown',
    avatarUrl: row.avatar_url || null,
    avatarViewUrl: row.avatar_view_url || null,
    referredBy: row.referred_by || null, // employee referral: listed first, any score
    email: row.email || null,
    currentTitle: row.current_title || null,
    currentCompany: row.current_company || null,
    experience: row.years_experience ?? null,
    score: row.match_score ?? null,
    band: row.band || null,
    matchedSkills: Array.isArray(row.matched_keywords) ? row.matched_keywords : [],
    missingSkills: Array.isArray(row.missing_keywords) ? row.missing_keywords : [],
    optionalMatched: Array.isArray(row.optional_matched) ? row.optional_matched : [],
    summary: row.summary || null,
    source: row.source || null,
    sourceLabel: SUGGESTED_SOURCE_LABEL[row.source] ?? row.source ?? '—',
    // Record that carries the assignment (inbox | form | manual) and its id.
    assignSource: row.assign_source || null,
    assignRecordId: row.assign_record_id || null,
    // assigned = assigned to THIS job; assignedJob* = whichever job holds them.
    assigned: Boolean(row.assigned),
    assignedJobId: row.assigned_job_post_id || null,
    assignedJobTitle: row.assigned_job_title || null,
    scoredAt: toDate(row.created_at),
  }
}

/**
 * Assign / unassign one Candidates-tab card on a job — PATCH /jobs/candidates/assign.
 * One job per candidate: assigning moves them off any other job server-side, so
 * the caller confirms that first. Requires jobs.edit.
 */
export function assignCandidate(jobPostId, { source, recordId, assigned }) {
  return request('/jobs/candidates/assign', {
    method: 'PATCH',
    params: { job_post_id: jobPostId },
    body: { source, record_id: recordId, assigned },
  })
}

/** Status-change audit for one requisition — GET /jobs/status-history/fetch. */
export function listStatusHistory(jobPostId) {
  return request('/jobs/status-history/fetch', { params: { job_post_id: jobPostId } })
}
